import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { alpha, styled as muiStyled, useTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import React, { useEffect, useRef } from 'react';
import {
  Menu,
  menuClasses,
  MenuItem,
  MenuItemStyles,
  Sidebar,
  SubMenu,
} from 'react-pro-sidebar';
import { Link, useLocation, useNavigate } from 'react-router';

import {
  getOpenGroupFromPathname,
  getSharedMainNavEntry,
  isMainNavChildActive,
  isSharedMainNavGroup,
  MAIN_SIDEBAR_PATHS,
  MainNavGroupId,
  pathMatches,
} from 'src/components/dashboard/mainSidebarNav';
import { CLIENT_BACKUP_PORTAL, CLIENT_BACKUPS, CLIENT_CASES, CLIENT_DASHBOARD, CLIENT_EDR_SOC, CLIENT_EXPLORER, CLIENT_HISTORY, CLIENT_LISTS, CLIENT_MINDTIME_SECURITY, CLIENT_MINDTIME_SECURITY_COMPLIANCE_SETTINGS, CLIENT_ORDERS, CLIENT_PRICES, CLIENT_REPORTING, CLIENT_SUBSCRIPTIONS, CLIENT_SUPPORT, CLIENT_USERS, CLIETN_COMPANIES } from 'src/constants/constants';
import { MINDTIME_PATHS } from 'src/constants/mindtime/constants';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import useAuth from 'src/hooks/useAuth';
import useHasPermissions from 'src/hooks/useHasPermission';
import { useAppDispatch } from 'src/hooks/useRedux';
import { CollapseIcon } from 'src/icons/CollapseIcon';
import CasesIcon from 'src/icons/Cases';
import CompaniesIcon from 'src/icons/companiesIcon';
import DashboardIcon from 'src/icons/Dashboard';
import OrdersIcon from 'src/icons/OrdersIcon';
// import ProjectsIcon from 'src/icons/Projects';
import ReportingIcon from 'src/icons/Reporting';
import Users from 'src/icons/Users';
import {
  getSecurityDefaultHrefForGroup,
  getSecurityGroupFromPathname,
  getSecurityLandingPath,
  getSecurityNavItemPermission,
  isSecurityNavGroupVisible,
  SECURITY_NAV_GROUPS,
  type SecurityNavAccessOptions,
} from 'src/mindtime/config/securityNav';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { useSubscriptionPermissionsContext } from 'src/mindtime/contexts/SubscriptionPermissionsContext';
import { useIsResellerOrg, useOrganizationTypeName } from 'src/mindtime/hooks/useIsResellerOrg';
import {
  buildSubscriptionAccessContext,
  canAccess,
  resolveClientSubscribedAccess,
  resolveEdrPermissionsReady,
} from 'src/mindtime/utils/subscriptionPermissions';
import MindtimeBackupsIcon from 'src/mindtime/icons/MindtimeBackupsIcon';
import MindtimeSecurityIcon from 'src/mindtime/icons/MindtimeSecurityIcon';
import { SubscriptionPermissionsNavSkeleton } from 'src/mindtime/pages/security/edr/shared/SubscriptionPermissionsSkeleton';
import {
  getAllFavouriteProjects,
  getAllProjects,
} from 'src/store/slices/projectsSlice';
import { getAllRoles } from 'src/store/slices/rolesSlice';

const SIDEBAR_ICON_INACTIVE = 'rgb(84, 110, 122)';
const NESTED_SECURITY_NAV_GROUPS = SECURITY_NAV_GROUPS.filter((g) => g.id !== 'soc');

const StyledSidebar = muiStyled(Sidebar)(({ theme }) => ({
  position: 'relative',
  '& .activeTabIndicator': { display: 'none' },
  // Hide parent labels when collapsed; flyout child labels stay visible
  '&.ps-collapsed > .ps-sidebar-container .main-nav-submenu > .ps-menu-button .ps-menu-label': {
    display: 'none !important',
  },
  '&.ps-collapsed .main-nav-submenu > .ps-menu-button': {
    justifyContent: 'center !important',
    paddingLeft: '0 !important',
    paddingRight: '0 !important',
    margin: '0 4px !important',
  },
  '&.ps-collapsed .main-nav-submenu > .ps-menu-button .ps-menu-icon': {
    marginRight: '0 !important',
  },
  '& .ps-sidebar-container': { overflowX: 'hidden' },

  // Expand chevron: shown for top-level nav groups only when expanded (rotation via renderExpandIcon)
  '& .ps-submenu-expand-icon': { display: 'none !important' },
  '& .main-nav-submenu > .ps-menu-button .ps-submenu-expand-icon': {
    display: 'flex !important',
    color: theme.palette.text.secondary,
  },
  '&.ps-collapsed .main-nav-submenu > .ps-menu-button .ps-submenu-expand-icon': {
    display: 'none !important',
  },

  // Parent (top-level) row
  '& .main-nav-submenu > .ps-menu-button': {
    height: '44px',
    margin: 0,
    paddingLeft: '16px',
    paddingRight: '8px',
    borderRadius: '8px',
  },
  '& .main-nav-submenu > .ps-menu-button:hover': {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.08)} !important`,
    color: theme.palette.primary.main,
  },
  '& .main-nav-submenu > .ps-menu-button:hover .ps-submenu-expand-icon': {
    color: theme.palette.primary.main,
  },
  // Parent is highlighted when its route (a child) is active
  '& .main-nav-submenu.main-nav-active > .ps-menu-button': {
    color: theme.palette.primary.main,
    backgroundColor: `${alpha(theme.palette.primary.main, 0.08)} !important`,
  },
  '& .main-nav-submenu.main-nav-active > .ps-menu-button .ps-menu-label': {
    fontWeight: 600,
  },
  '& .main-nav-submenu.main-nav-active > .ps-menu-button .ps-submenu-expand-icon': {
    color: theme.palette.primary.main,
  },

  // Children container (expanded): hide when closed; transparent when open
  '&:not(.ps-collapsed) .main-nav-submenu:not(.ps-open) .ps-submenu-content': {
    display: 'none !important',
    height: '0 !important',
    overflow: 'hidden !important',
    padding: '0 !important',
    margin: '0 !important',
    border: 'none !important',
  },
  '&:not(.ps-collapsed) .main-nav-submenu.ps-open .ps-submenu-content': {
    backgroundColor: 'transparent',
    margin: 0,
    padding: 0,
  },

  // Collapsed: library popper flyout beside the parent icon
  '&.ps-collapsed .main-nav-submenu .ps-submenu-content': {
    width: '180px !important',
    backgroundColor: '#FFFFFF !important',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px',
    boxShadow: '0 4px 16px rgba(15, 23, 42, 0.12)',
    padding: '6px 0',
    zIndex: 1300,
  },

  // Child (sub-tab) row — indented when expanded
  '& .main-nav-submenu .ps-submenu-content .ps-menu-button': {
    height: '40px',
    minHeight: '40px',
    margin: '0 !important',
    paddingLeft: '40px !important',
    paddingRight: '8px',
    borderRadius: '8px',
  },
  '& .main-nav-submenu .ps-submenu-content .ps-menu-label': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#718096',
  },
  // Selected sub-tab: tinted fill + bolder near-black text (text stays non-primary)
  '& .main-nav-submenu .ps-submenu-content .ps-menu-button.ps-active': {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.08)} !important`,
    borderRadius: '8px',
  },
  '& .main-nav-submenu .ps-submenu-content .ps-menu-button.ps-active .ps-menu-label': {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  // Left accent bar on the active sub-tab
  '& .main-nav-submenu .ps-submenu-content .ps-menu-button.ps-active::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '8px',
    width: '4px',
    height: '60%',
    backgroundColor: theme.palette.primary.main,
  },
  // Hover wins over selected for text color: text turns primary on hover only
  '& .main-nav-submenu .ps-submenu-content .ps-menu-button:hover': {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.08)} !important`,
  },
  '& .main-nav-submenu .ps-submenu-content .ps-menu-button:hover .ps-menu-label': {
    color: theme.palette.primary.main,
  },
  // Collapsed flyout child rows: compact, left-aligned labels
  '&.ps-collapsed .main-nav-submenu .ps-submenu-content .ps-menu-button': {
    height: '40px',
    minHeight: '40px',
    justifyContent: 'flex-start !important',
    margin: '2px 6px !important',
    paddingLeft: '12px !important',
    paddingRight: '12px !important',
  },
  '&.ps-collapsed .main-nav-submenu .ps-submenu-content .ps-menu-button.ps-active::before': {
    top: '8px',
    height: '60%',
  },
}));

const DashboardSidebar: React.FC = () => {
  const checkPermissions = useHasPermissions();
  const can = (...perms: string[]) => perms.some((p) => checkPermissions([p]));
  const { userData } = useUserProfile();
  const isReseller = useIsResellerOrg();
  const organizationTypeName = useOrganizationTypeName();
  const theme = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, isInitialized } = useAuth();

  const [collapsed, setCollapsed] = React.useState(false);
  const [toggled, setToggled] = React.useState(false);
  const [openGroup, setOpenGroup] = React.useState<MainNavGroupId | null>(() =>
    getOpenGroupFromPathname(pathname)
  );
  const sharedGroupIntentRef = useRef<MainNavGroupId | null>(null);

  const pathActiveGroup = getOpenGroupFromPathname(pathname);
  const activeSecurityGroup = getSecurityGroupFromPathname(pathname);
  const isSecurityRoute = pathname.includes(MINDTIME_PATHS.SECURITY);
  const sharedEntry = getSharedMainNavEntry(pathname);
  const activeGroup: MainNavGroupId | null =
    sharedEntry &&
      sharedGroupIntentRef.current &&
      sharedEntry.groups.includes(sharedGroupIntentRef.current)
      ? sharedGroupIntentRef.current
      : pathActiveGroup;

  const { subscriptionKeys, hasActiveSubscription, resolved: subscriptionResolved, roleKeys, orgEntitlements } =
    useSubscriptionPermissionsContext();

  const clientSubscribed = resolveClientSubscribedAccess(
    isReseller,
    orgEntitlements,
    hasActiveSubscription,
  );

  const permissionsReady = resolveEdrPermissionsReady({
    isReseller,
    authInitialized: isInitialized,
    hasAuthUser: Boolean(user),
    subscriptionResolved: subscriptionResolved,
  });

  const securityNavAccess: SecurityNavAccessOptions = {
    subscriptionKeys,
    roleKeys,
    hasActiveSubscription: clientSubscribed,
    subscriptionResolved: subscriptionResolved,
    isReseller,
    organizationTypeName,
    permissionsReady,
  };

  const socPermission =
    getSecurityNavItemPermission(
      { id: 'security-operations-center', label: '', path: '' },
      EDR_SOLUTION_ID,
    ) ?? CLIENT_EDR_SOC;
  const canSeeSecurityOverview =
    permissionsReady &&
    canAccess(
      buildSubscriptionAccessContext({
        isReseller,
        organizationTypeName,
        orgEntitlements,
        hasActiveSubscription,
        roleKeys,
        subscriptionKeys,
        checkRole: checkPermissions,
      }),
      socPermission,
      { blockPreviewCreate: true },
    );
  const canDashboard = can(CLIENT_DASHBOARD);
  const visibleNestedSecurityGroups = permissionsReady
    ? NESTED_SECURITY_NAV_GROUPS.filter((group) =>
        isSecurityNavGroupVisible(group, checkPermissions, isReseller, securityNavAccess),
      )
    : [];
  const securityLandingPath = permissionsReady
    ? getSecurityLandingPath(checkPermissions, isReseller, securityNavAccess)
    : undefined;
  const canSeeComplianceSettings = can(CLIENT_MINDTIME_SECURITY_COMPLIANCE_SETTINGS);
  // Reseller role hierarchy: Client Mindtime Security → EDR/SOC group → module → widgets.
  const canSeeSecurityArea = !isReseller || can(CLIENT_MINDTIME_SECURITY);
  const subscriptionPending = canSeeSecurityArea && !permissionsReady;
  const showSecurityGroup =
    canSeeSecurityArea &&
    (subscriptionPending ||
      canSeeComplianceSettings ||
      (permissionsReady &&
        (canSeeSecurityOverview || visibleNestedSecurityGroups.length > 0)));

  const menuItemStyles: MenuItemStyles = {
    root: {
      fontSize: '14px',
      fontFamily: 'Inter',
      fontWeight: 500,
      '& .activeTabIndicator': { display: 'none' },
      [`&.${menuClasses.menuItemRoot}`]: {
        marginBottom: 0,
        color: '#718096',
        '& a': { textDecoration: 'none' },
      },
    },
    icon: { width: 'auto', minWidth: 24, height: 'auto', marginRight: 10, lineHeight: 1 },
    SubMenuExpandIcon: {},
    label: { fontSize: '14px', fontWeight: 500 },
    button: {
      position: 'relative',
      height: '40px',
    },
  };

  useEffect(() => {
    const entry = getSharedMainNavEntry(pathname);
    if (
      entry &&
      sharedGroupIntentRef.current &&
      entry.groups.includes(sharedGroupIntentRef.current)
    ) {
      setOpenGroup(sharedGroupIntentRef.current);
      return;
    }
    sharedGroupIntentRef.current = null;
    setOpenGroup(getOpenGroupFromPathname(pathname));
  }, [pathname]);

  useEffect(() => {
    dispatch(getAllProjects());
    dispatch(getAllFavouriteProjects());
    dispatch(
      getAllRoles({
        search: '',
        page: 0,
        limit: 20,
        loginUserRoleIdentifier: user?.role?.[0]?.identifier,
      })
    );
  }, [dispatch, user?.role]);

  const withNavTooltip = (label: string, node: React.ReactElement) =>
    collapsed ? (
      <Tooltip title={label} placement="right" arrow>
        <span style={{ display: 'block', width: '100%' }}>{node}</span>
      </Tooltip>
    ) : (
      node
    );

  const iconColor = (active: boolean) =>
    active ? theme.palette.primary.main : SIDEBAR_ICON_INACTIVE;

  const securityDefaultPath =
    (clientSubscribed && securityLandingPath) ||
    (canSeeSecurityOverview && MAIN_SIDEBAR_PATHS.SECURITY_OVERVIEW) ||
    (canSeeComplianceSettings && MAIN_SIDEBAR_PATHS.SECURITY_COMPLIANCE_SETTINGS) ||
    (visibleNestedSecurityGroups[0] &&
      getSecurityDefaultHrefForGroup(
        visibleNestedSecurityGroups[0],
        checkPermissions,
        isReseller,
        securityNavAccess,
      )) ||
    undefined;

  const handleGroupOpenChange =
    (groupId: MainNavGroupId, defaultTo?: string) => (open: boolean) => {
      if (open) {
        setOpenGroup(groupId);
        sharedGroupIntentRef.current = isSharedMainNavGroup(groupId) ? groupId : null;
        if (defaultTo && pathActiveGroup !== groupId) navigate(defaultTo);
      } else if (openGroup === groupId) {
        setOpenGroup(null);
        if (sharedGroupIntentRef.current === groupId) sharedGroupIntentRef.current = null;
      }
    };

  const renderChild = (label: string, to: string, groupId?: MainNavGroupId) => (
    <MenuItem
      key={`${groupId ?? 'nav'}-${to}`}
      className="main-nav-child"
      component={<Link to={to} />}
      active={isMainNavChildActive(pathname, to)}
      onClick={() => {
        if (!groupId || !getSharedMainNavEntry(to)) return;
        sharedGroupIntentRef.current = groupId;
        setOpenGroup(groupId);
      }}
    >
      {label}
    </MenuItem>
  );

  const renderNavGroup = ({
    id,
    label,
    icon,
    defaultTo,
    visible,
    children,
  }: {
    id: MainNavGroupId;
    label: string;
    icon: React.ReactNode;
    defaultTo?: string;
    visible: boolean;
    children: React.ReactNode;
  }) => {
    if (!visible) return null;
    return withNavTooltip(
      label,
      <SubMenu
        key={id}
        className={`main-nav-submenu${activeGroup === id ? ' main-nav-active' : ''}`}
        label={label}
        style={{ marginBottom: '2px' }}
        icon={icon}
        open={openGroup === id}
        onOpenChange={handleGroupOpenChange(id, defaultTo)}
        active={activeGroup === id}
      >
        {children}
      </SubMenu>
    );
  };

  return (
    <Box style={{ display: 'flex', height: '100%', position: 'relative' }}>
      <StyledSidebar
        collapsed={collapsed}
        backgroundColor="#FFFFFF"
        transitionDuration={600}
        toggled={toggled}
        onBackdropClick={() => setToggled(false)}
        width="200px"
        collapsedWidth="75px"
        breakPoint="md"
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingTop: '4px',
              paddingRight: '8px',
              paddingLeft: 0,
              paddingBottom: '8px',
            }}
          >
            <Menu
              closeOnClick
              menuItemStyles={menuItemStyles}
              renderExpandIcon={({ open }) => (
                <KeyboardArrowDownIcon
                  style={{
                    fontSize: 28,
                    transition: 'transform 0.3s ease',
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              )}
            >
              {renderNavGroup({
                id: 'dashboard',
                label: 'Dashboard',
                defaultTo: canDashboard
                  ? MAIN_SIDEBAR_PATHS.DASHBOARD
                  : canSeeSecurityOverview
                    ? MAIN_SIDEBAR_PATHS.SECURITY_OVERVIEW
                    : undefined,
                visible: canDashboard || canSeeSecurityOverview,
                icon: <DashboardIcon color={iconColor(activeGroup === 'dashboard')} />,
                children: (
                  <>
                    {/* These are the actual dashboard routes used by the application */}
                    {canDashboard &&
                      renderChild('Dashboard', MAIN_SIDEBAR_PATHS.DASHBOARD, 'dashboard')}
                    {/* Under development — hidden from sidebar for now
                    {canDashboard && (
                      <>
                        {renderChild('SSL Overview', MAIN_SIDEBAR_PATHS.SSL_OVERVIEW)}
                        {renderChild('Backup Overview', MAIN_SIDEBAR_PATHS.BACKUP_OVERVIEW_DASH)}
                        {renderChild('Dynamic Dashboard', MAIN_SIDEBAR_PATHS.DYNAMIC_DASHBOARD)}
                      </>
                    )}
                    */}
                    {canSeeSecurityOverview &&
                      renderChild(
                        'Security Overview',
                        MAIN_SIDEBAR_PATHS.SECURITY_OVERVIEW,
                        'dashboard'
                      )}
                  </>
                ),
              })}

              {renderNavGroup({
                id: 'clients',
                label: 'Clients',
                visible: can(CLIETN_COMPANIES),
                icon: <CompaniesIcon color={iconColor(activeGroup === 'clients')} />,
                children: (
                  <>
                    {can(CLIETN_COMPANIES) &&
                      renderChild('Clients', MAIN_SIDEBAR_PATHS.CLIENTS)}
                    {/* Hidden for now
                    {can(CLIENT_BACKUP_USERS) &&
                      renderChild('My Clients', MAIN_SIDEBAR_PATHS.MY_CLIENTS)} */}
                  </>
                ),
              })}

              {renderNavGroup({
                id: 'backup',
                label: 'Backup',
                visible: can(CLIENT_BACKUPS, CLIENT_BACKUP_PORTAL),
                icon: <MindtimeBackupsIcon color={iconColor(activeGroup === 'backup')} />,
                children: (
                  <>
                    {/* Under development — hidden from sidebar for now
                    {can(CLIENT_BACKUPS) &&
                      renderChild('Overview', MAIN_SIDEBAR_PATHS.BACKUP_OVERVIEW)}
                    */}
                    {can(CLIENT_BACKUPS) &&
                      renderChild('My Backups', MAIN_SIDEBAR_PATHS.MY_BACKUPS)}
                    {can(CLIENT_BACKUP_PORTAL) &&
                      renderChild('Backup Portal', MAIN_SIDEBAR_PATHS.BACKUP_PORTAL)}
                  </>
                ),
              })}

              {renderNavGroup({
                id: 'security',
                label: 'Security',
                defaultTo: securityDefaultPath,
                visible: showSecurityGroup,
                icon: (
                  <MindtimeSecurityIcon
                    color={iconColor(activeGroup === 'security' || isSecurityRoute)}
                  />
                ),
                children: (
                  <>
                    {!permissionsReady ? (
                      <SubscriptionPermissionsNavSkeleton rows={4} />
                    ) : (
                      <>
                        {canSeeSecurityOverview &&
                          renderChild(
                            'SOC Overview',
                            MAIN_SIDEBAR_PATHS.SECURITY_OVERVIEW,
                            'security'
                          )}
                        {visibleNestedSecurityGroups.map((group) => (
                          <MenuItem
                            key={group.id}
                            className="main-nav-child"
                            component={
                              <Link
                                to={getSecurityDefaultHrefForGroup(
                                  group,
                                  checkPermissions,
                                  isReseller,
                                  securityNavAccess,
                                )}
                              />
                            }
                            active={
                              isSecurityRoute &&
                              !pathMatches(
                                pathname,
                                MAIN_SIDEBAR_PATHS.SECURITY_COMPLIANCE_SETTINGS,
                              ) &&
                              activeSecurityGroup.id === group.id
                            }
                          >
                            {group.label}
                          </MenuItem>
                        ))}
                      </>
                    )}
                    {canSeeComplianceSettings &&
                      renderChild(
                        'Compliance Settings',
                        MAIN_SIDEBAR_PATHS.SECURITY_COMPLIANCE_SETTINGS,
                      )}
                    {/* Under development — hidden from sidebar for now
                    {can(CLIENT_MINDTIME_SECURITY_SSL_MANAGEMENT) &&
                      renderChild('SSL', MAIN_SIDEBAR_PATHS.SECURITY_SSL)}
                    */}
                  </>
                ),
              })}

              {renderNavGroup({
                id: 'support',
                label: 'Support',
                visible: can(CLIENT_CASES, CLIENT_HISTORY, CLIENT_SUPPORT, CLIENT_EXPLORER),
                icon: <CasesIcon color={iconColor(activeGroup === 'support')} />,
                children: (
                  <>
                    {can(CLIENT_SUPPORT, CLIENT_EXPLORER) && (
                      <>
                        {renderChild('Knowledge Base', MAIN_SIDEBAR_PATHS.SUPPORT_KB)}
                        {renderChild(
                          'Service Status',
                          MAIN_SIDEBAR_PATHS.SUPPORT_SERVICE_STATUS
                        )}
                        {renderChild('Downloads', MAIN_SIDEBAR_PATHS.SUPPORT_DOWNLOADS)}
                      </>
                    )}
                    {can(CLIENT_CASES) && renderChild('Tickets', MAIN_SIDEBAR_PATHS.CASES)}
                    {can(CLIENT_HISTORY) && renderChild('History', MAIN_SIDEBAR_PATHS.HISTORY)}
                  </>
                ),
              })}

              {/* Projects group — hidden from sidebar for now
              {renderNavGroup({
                id: 'projects',
                label: 'Projects',
                defaultTo: showProjectItems
                  ? MAIN_SIDEBAR_PATHS.DASHBOARD
                  : MAIN_SIDEBAR_PATHS.PLANNING,
                visible: showProjectItems || showPlanning,
                icon: <ProjectsIcon color={iconColor(activeGroup === 'projects')} />,
                children: (
                  <>
                    {showProjectItems && (
                      <>
                        {renderChild(
                          'Dashboard',
                          MAIN_SIDEBAR_PATHS.DASHBOARD,
                          'projects'
                        )}
                        {renderChild('Projects', MAIN_SIDEBAR_PATHS.PROJECTS)}
                      </>
                    )}
                    {showPlanning && renderChild('Planning', MAIN_SIDEBAR_PATHS.PLANNING)}
                  </>
                ),
              })}
              */}

              {renderNavGroup({
                id: 'reports',
                label: 'Reports',
                visible: can(CLIENT_REPORTING),
                icon: <ReportingIcon color={iconColor(activeGroup === 'reports')} />,
                children: renderChild('Reports', MAIN_SIDEBAR_PATHS.REPORTS),
              })}

              {renderNavGroup({
                id: 'billing',
                label: 'Billing',
                visible: can(CLIENT_SUBSCRIPTIONS, CLIENT_ORDERS, CLIENT_PRICES),
                icon: <OrdersIcon color={iconColor(activeGroup === 'billing')} />,
                children: (
                  <>
                    {can(CLIENT_SUBSCRIPTIONS) && renderChild('Subscriptions', MAIN_SIDEBAR_PATHS.SUBSCRIPTIONS)}
                    {can(CLIENT_ORDERS) && renderChild('Orders', MAIN_SIDEBAR_PATHS.ORDERS)}
                    {can(CLIENT_PRICES) && renderChild('Prices', MAIN_SIDEBAR_PATHS.PRICES)}
                  </>
                ),
              })}

              {renderNavGroup({
                id: 'administration',
                label: 'Administration',
                visible: can(CLIENT_USERS, CLIENT_LISTS),
                icon: <Users color={iconColor(activeGroup === 'administration')} />,
                children: (
                  <>
                    {can(CLIENT_USERS) && renderChild('Users', MAIN_SIDEBAR_PATHS.USERS)}
                    {can(CLIENT_LISTS) && renderChild('Lists', MAIN_SIDEBAR_PATHS.LISTS)}
                    {/* Under development — hidden from sidebar for now
                    {can(CLIENT_USERS) && (
                      <>
                        {renderChild('Roles & Permissions', MAIN_SIDEBAR_PATHS.ROLES)}
                        {renderChild('Audit Log', MAIN_SIDEBAR_PATHS.AUDIT_LOG)}
                      </>
                    )}
                    */}
                  </>
                ),
              })}

              {/* Home (/mindtime/home) — hidden from sidebar for now */}
            </Menu>
          </div>

          <Box
            role="button"
            tabIndex={0}
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'space-between',
              color: '#425466',
              cursor: 'pointer',
              padding: '16px 12px',
              fontSize: '14px',
              fontWeight: 600,
              fontFamily: 'Inter',
              borderTop: '1px solid',
              borderColor: '#EDF2F7',
              margin: '0 4px',
              '&:hover': {
                backgroundColor: `${alpha(theme.palette.primary.main, 0.15)}`,
                borderRadius: '8px',
              },
            }}
            onClick={() => setCollapsed((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setCollapsed((v) => !v);
              }
            }}
          >
            {!collapsed && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CollapseIcon color={SIDEBAR_ICON_INACTIVE} />
                <span>Collapse</span>
              </div>
            )}
            <KeyboardArrowRightIcon
              style={{
                fontSize: 28,
                color: SIDEBAR_ICON_INACTIVE,
                transition: 'transform ease 0.6s',
                transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
              }}
            />
          </Box>
        </div>
        <div className="activeTabIndicator" />
      </StyledSidebar>
    </Box>
  );
};

export default DashboardSidebar;
